import { api } from './client';
import { endpoints } from './endpoints';
import { ApiError } from '@/types/api';

/**
 * Conversations, from whichever side the caller is on.
 *
 * There is one set of calls rather than a student set and a company set: the
 * backend resolves the side from the caller's own account, so a second module
 * here would only be a second place for the two to drift apart.
 */
export function listConversations(params) {
  return api.getPaginated(endpoints.messages.root, { params });
}

export function getUnreadMessages() {
  return api.get(endpoints.messages.unreadCount);
}

export function getConversation(id, params) {
  return api.get(endpoints.messages.byId(id), { params });
}

/** Starts the thread if there is not one. A conversation has no other origin. */
export function sendAboutApplication(applicationId, body) {
  return api.post(endpoints.messages.byApplication(applicationId), { body });
}

/** A 404 here means "nobody has written yet", which is an answer rather than an error. */
async function orNull(request) {
  try {
    return await request;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** The thread about an application, or null until somebody sends the first message. */
export function getApplicationConversation(applicationId, params) {
  return orNull(api.get(`/messages/application/${applicationId}`, { params }));
}

/** A student's private question about a listing, asked before applying. Asking again continues it. */
export function askAboutInternship(internshipId, body) {
  return api.post(`/messages/internship/${internshipId}`, { body });
}

/** The student's question thread for a listing, or null when they have not asked. */
export function getInternshipQuestion(internshipId) {
  return orNull(api.get(`/messages/internship/${internshipId}`));
}

/** A company writing to a candidate found in search. Writing again continues the thread. */
export function messageCandidate(studentId, body) {
  return api.post(`/messages/candidate/${studentId}`, { body });
}

/** The company's thread with a candidate, or null until it writes. */
export function getCandidateConversation(studentId) {
  return orNull(api.get(`/messages/candidate/${studentId}`));
}

export function sendMessage(conversationId, body) {
  return api.post(endpoints.messages.byId(conversationId), { body });
}

/** An attachment is its own message, so this is a send rather than an upload. */
export function sendAttachment(conversationId, file, caption = '') {
  const form = new FormData();
  form.append('file', file);
  form.append('caption', caption);
  return api.upload(endpoints.messages.attachments(conversationId), form);
}

export function getAttachmentLink(conversationId, attachmentId) {
  return api.get(endpoints.messages.attachmentDownload(conversationId, attachmentId));
}

export function markConversationRead(conversationId) {
  return api.post(endpoints.messages.read(conversationId));
}

export function reportConversation(conversationId, reason) {
  return api.post(endpoints.messages.report(conversationId), { reason });
}

/**
 * A short-lived credential for the socket handshake.
 *
 * The browser WebSocket API cannot set an Authorization header, so the socket
 * is opened with this in the query string instead. It expires in seconds and
 * grants nothing but the event stream.
 */
export function getRealtimeTicket() {
  return api.post(endpoints.realtime.ticket);
}
